import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { CONSTANTS } from '../constants/Constants';

const CurrentAffairsContext = createContext();

export const useCurrentAffairs = () => useContext(CurrentAffairsContext);

export const CurrentAffairsProvider = ({ children }) => {
  const [dailyAffairs, setDailyAffairs] = useState([]);
  const [monthlyCompilations, setMonthlyCompilations] = useState([]);
  const [currentAffair, setCurrentAffair] = useState(null);
  const [monthlyCompilation, setMonthlyCompilation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    category: 'all',
    month: 'all',
    year: new Date().getFullYear()
  });
  const [pagination, setPagination] = useState({
    page: 1,
    pages: 1,
    total: 0
  });

  // Fetch daily current affairs with filters
  const fetchDailyAffairs = async (category = 'all', page = 1, limit = 9) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axios.get(`${CONSTANTS.APIURL}/api/current-affairs`, {
        params: {
          category,
          page,
          limit,
          sort: '-date'
        }
      });
      
      if (response.data.success) {
        setDailyAffairs(response.data.data);
        setPagination({
          page: response.data.page,
          pages: response.data.pages,
          total: response.data.total
        });
        setFilters(prev => ({ ...prev, category }));
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch current affairs');
      console.error('Error fetching daily affairs:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch monthly compilations with filters
  const fetchMonthlyCompilations = async (month = 'all', year, page = 1, limit = 6) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axios.get(`${CONSTANTS.APIURL}/api/monthly-compilations`, {
        params: {
          month,
          year,
          page,
          limit,
          sort: '-year -month'
        }
      });
      
      if (response.data.success) {
        setMonthlyCompilations(response.data.data);
        setPagination({
          page: response.data.page,
          pages: response.data.pages,
          total: response.data.total
        });
        setFilters(prev => ({ ...prev, month }));
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch monthly compilations');
      console.error('Error fetching monthly compilations:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch a single current affair by ID
  const fetchCurrentAffairById = async (id) => {
    try {
      setLoading(true);
      setError(null);
      setCurrentAffair(null);
      
      const response = await axios.get(`${CONSTANTS.APIURL}/api/current-affairs/${id}`);
      
      if (response.data.success) {
        setCurrentAffair(response.data.data);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch current affair');
      console.error('Error fetching current affair by ID:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch a single monthly compilation by ID
  const fetchMonthlyCompilationById = async (id) => {
    try {
      setLoading(true);
      setError(null);
      setMonthlyCompilation(null);
      
      const response = await axios.get(`${CONSTANTS.APIURL}/api/monthly-compilations/${id}`);
      
      if (response.data.success) {
        setMonthlyCompilation(response.data.data);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch monthly compilation');
      console.error('Error fetching monthly compilation by ID:', err);
    } finally {
      setLoading(false);
    }
  };

  // Clear current affair (for cleanup)
  const clearCurrentAffair = () => {
    setCurrentAffair(null);
  };

  // Clear monthly compilation (for cleanup)
  const clearMonthlyCompilation = () => {
    setMonthlyCompilation(null);
  };

  // Initialize data
  useEffect(() => {
    fetchDailyAffairs();
  }, []);

  return (
    <CurrentAffairsContext.Provider
      value={{
        dailyAffairs,
        monthlyCompilations,
        currentAffair,
        monthlyCompilation,
        loading,
        error,
        filters,
        pagination,
        fetchDailyAffairs,
        fetchMonthlyCompilations,
        fetchCurrentAffairById,
        fetchMonthlyCompilationById,
        clearCurrentAffair,
        clearMonthlyCompilation,
        setFilters
      }}
    >
      {children}
    </CurrentAffairsContext.Provider>
  );
};

export default CurrentAffairsProvider;